/* State module for itemperties information, shared across the app. If a page needs to store specific information, use a module for the given page. */

// Mutations and Actions
import { PAGE_SIZES } from '@/store/baseModule/constants';
import {
  isAttributeEqualToRef,
  isAttributeObjDirty,
  isAttributesObjEqualToRef,
  isSearchDirty,
} from '@/store/baseModule/utils';
import BaseService, { Service } from '@/services/services/tools/baseService';
import BaseAPI from '@/services/api/internal/tools/baseAPI';
import { Module } from 'vuex';
import BaseState from '@/store/baseModule/baseModule';
import actions, { MakeHttpParamsUrlType, MakeQueryParamsType } from './actions';
import mutations from './mutations';

export interface BaseModuleOpts<T> {
  attributesObjRef?: any;
  attributesObj?: any;
  makeQueryParams?: MakeQueryParamsType;
  makeHttpParamsUrl?: MakeHttpParamsUrlType;
  serviceAPI: Service<T>;
  sortChoices?: string[];
  defaultSortBy?: string[];
  customGetters?: any;
  customMutations?: any;
  customActions?: any;
  defaultASC?: boolean[];
  searchAttributesArray?: string[];
  dateMin?: any;
  pageSizeIndex?: number;
  hasDefaultFilters?: boolean;
}

export default class BaseModule<T> implements Module<BaseState<T>, any> {
  mutations: any;

  namespaced = true;

  state;

  actions;

  constructor(opts: BaseModuleOpts<T>) {
    this.state = new BaseState<T>({
      attributesObjRef: opts.attributesObjRef || {},
      attributesObj: opts.attributesObj || {},
      dateMin: opts.dateMin || null,
      defaultASC: opts.defaultASC,
      defaultSortBy: opts.defaultSortBy,
      pageSizeIndex: opts.pageSizeIndex,
      hasDefaultFilters: opts.hasDefaultFilters,
      searchAttributesArray: opts.searchAttributesArray || [],
      sortChoices: opts.sortChoices || [],
    });
    this.mutations = mutations({ customMutations: opts.customMutations });
    this.getters = {
      ...this.getters,
      ...opts.customGetters,
    };
    this.actions = actions({
      makeHttpParamsUrl: opts.makeHttpParamsUrl,
      customActions: opts.customActions,
      makeQueryParams: opts.makeQueryParams,
      serviceAPI:
        opts.serviceAPI ||
        new BaseService({
          api: new BaseAPI({}),
        }),
      defaultSortBy: opts.defaultSortBy,
      defaultASC: opts.defaultASC,
    });
  }

  getters = {
    attributesObjRef: (state) => state.attributesObjRef.data,
    sort: (state) => state.items.sort,
    lastRefreshItemsDate: (state) => state.items.lastRefreshItemsDate,
    requestItems: (state) => state.items.request,
    requestQueryParams: (state) => state.items.queryParams,
    isEditMode: (state) => state.items.isEditMode,
    sortBy: (state, getters) => getters.sort.by,
    sortASC: (state, getters) => getters.sort.ASC,
    page: (state) => state.items.page,
    hasDefaultFilters: (state) => !!state.defaultFilters,
    pageSizeIndex: (state) => state.items.pageSizeIndex,
    pageSize: (state) => PAGE_SIZES[state.items.pageSizeIndex],
    isViewTable: (state) => state.items.isViewTable,
    attributesObj: (state) => state.filters.attributesObj,
    attributesObjDefaultFilters: (state) => state.defaultFilters.attributesObj,
    attributeKeys: (state, getters) =>
      Object.keys(getters.attributesObjRef).filter(
        (key) => !getters.attributesObjRef[key].noFilter
      ),
    isAttributesObjEqualToRef: (state, getters) =>
      isAttributesObjEqualToRef({
        attributesObjRef: getters.attributesObjRef,
        attributesObj: getters.attributesObj,
      }),
    isAttributeEqualToRef: (state, getters) => (attribute) =>
      isAttributeEqualToRef({
        attributesObjRef: getters.attributesObjRef,
        attributesObj: getters.attributesObj,
        attribute,
      }),
    isAttributeObjDirty: (state, getters) =>
      isAttributeObjDirty({
        attributesObjRef: getters.attributesObjRef,
        attributesObj: getters.attributesObj,
      }),
    isSearchDirty: (state, getters) =>
      isSearchDirty({ and: getters.search.$and, or: getters.search.$or }),
    isDatesDirty: (state) =>
      [state.filters.dateMin, state.filters.dateMax].filter((el) => !!el)
        .length,
    attributeKeyName: (state, getters) => (attribute, id) =>
      getters.attributesObjRef[attribute] &&
      getters.attributesObjRef[attribute].values.find(
        (value) => value.id === id
      ) &&
      getters.attributesObjRef[attribute].values.find(
        (value) => value.id === id
      ).name,
    clientSideAttributeKeys: (state, getters) =>
      getters.attributesObjRef
        ? Object.keys(getters.attributesObjRef).filter(
            (key) =>
              !getters.attributesObjRef[key].serverSide &&
              !getters.attributesObjRef[key].noFilter
          )
        : [],
    serverSideAttributeKeys: (state, getters) =>
      getters.attributesObj
        ? Object.keys(getters.attributesObj).filter(
            (key) => getters.attributesObj[key].serverSide
          )
        : [],
    search: (state) => state.filters.search,
    globalSearch: (state) => state.filters.globalSearch,
    searchAttributesArray: (state) => state.filters.searchAttributesArray,
    urlParamId: (state) => state.filters.urlParamId,
    dateMin: (state) => state.filters.dateMin,
    dateMax: (state) => state.filters.dateMax,
    current: (state) => state.current.data,
    isLoadingItems: (state) => state.items.isLoading,
    isLoadingCurrent: (state) => state.current.isLoading,
    isLoadingAttributesObjRef: (state) => state.attributesObjRef.isLoading,
    itemsHasAttributeValueNumber: (state, getters) => (attribute, id) => {
      if (
        getters.attributesObj[attribute] &&
        getters.attributesObj[attribute].type === '$null'
      ) {
        return id === 'O'
          ? state.items.data.filter((item) => !!item[attribute]).length
          : state.items.data.filter((item) => !item[attribute]).length;
      }
      return state.items.data.filter((item) => item[attribute] === id).length;
    },
    items: (state, getters) =>
      state.items.data.filter((item) =>
        getters.clientSideAttributeKeys.every((attribute) =>
          getters.attributesObjRef[attribute].values.includes(item[attribute])
        )
      ),
    error: (state) => state.items.error,
    isFiltersDirty: (state, getters) =>
      getters.isAttributeObjDirty +
      getters.isSearchDirty +
      getters.isDatesDirty,
  };
}
